import type { ReactNode } from 'react';
import { Icon, SectionCard, type IconName } from '../../../components/ui';
import { Delta, type SentidoDoDelta, type TipoDeMedida } from './Delta';

/**
 * Faixa de retorno dos disparos: cada medida com o numero, a variacao contra
 * o periodo anterior e UMA linha dizendo o que ela significa em termos de
 * negocio.
 *
 * Decisoes nao obvias:
 *  1. A explicacao e texto VISIVEL (`apoio`), nao so tooltip: no celular nao
 *     ha hover, e "custo por recuperacao" sem definicao e so um numero.
 *  2. `destaque` pinta a borda de marca em UMA medida (a principal da
 *     tela); o resto fica neutro para a hierarquia existir.
 *  3. O valor chega PRONTO (formatado). `atual`/`anterior` sao os inteiros
 *     crus so para o Delta calcular a variacao.
 */

export interface MedidaDeRetorno {
  readonly chave: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly atual: number;
  readonly anterior: number | null;
  readonly tipo: TipoDeMedida;
  readonly sentido?: SentidoDoDelta;
  readonly apoio: string;
  readonly icone: IconName;
  readonly destaque?: boolean;
  /** Texto no lugar da variacao quando nao ha como comparar (ex.: sem pagamento ainda). */
  readonly semComparacao?: string;
}

export function GradeDeRetorno({
  id,
  titulo,
  descricao,
  medidas,
  aside,
  rodape,
}: {
  readonly id: string;
  readonly titulo: string;
  readonly descricao: ReactNode;
  readonly medidas: readonly MedidaDeRetorno[];
  readonly aside?: ReactNode;
  readonly rodape?: ReactNode;
}): JSX.Element {
  return (
    <SectionCard id={id} title={titulo} icon="grafico" description={descricao} aside={aside}>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {medidas.map((m) => (
          <li
            key={m.chave}
            className={`flex min-w-0 flex-col gap-1 rounded-xl border px-4 py-3 transition-colors ${m.destaque === true ? 'border-brand/40 bg-brand/5' : 'border-line bg-elevated hover:border-edge'}`}
          >
            <p className="flex items-center gap-1.5 text-xs font-medium text-ink-muted">
              <Icon name={m.icone} size="xs" className={m.destaque === true ? 'text-brand' : 'text-ink-subtle'} />
              {m.rotulo}
            </p>
            <p className="truncate text-2xl font-semibold tracking-tight text-ink">{m.valor}</p>
            {m.semComparacao === undefined ? <Delta atual={m.atual} anterior={m.anterior} tipo={m.tipo} sentido={m.sentido} /> : <span className="text-xs text-ink-subtle">{m.semComparacao}</span>}
            <p className="text-xs text-ink-subtle">{m.apoio}</p>
          </li>
        ))}
      </ul>
      {rodape === undefined ? null : <div className="mt-3">{rodape}</div>}
    </SectionCard>
  );
}
